import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/shiksha-db';
import { auth } from '../../services/firebase';
import { ArrowLeft, FileText, CheckSquare, Square, DownloadCloud } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { ClassReportTemplate } from '../../components/ClassReportTemplate';

export function ReportGenerator() {
  const [academicYear, setAcademicYear] = useState('2026-27');
  const [targetClass, setTargetClass] = useState('Class IX');
  const [section, setSection] = useState('A');
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  
  const reportTemplateRef = useRef(null);

  const [masterStudents, setMasterStudents] = useState([]);
  const [masterMarks, setMasterMarks] = useState([]);
  const [isLoadingMasterData, setIsLoadingMasterData] = useState(false);
  const [masterDataError, setMasterDataError] = useState(null);

  useEffect(() => {
    async function fetchMasterData() {
      setIsLoadingMasterData(true);
      setMasterDataError(null);
      try {
        if (!auth.currentUser) throw new Error("User not authenticated");
        const idToken = await auth.currentUser.getIdToken(true);
        const API_URL = import.meta.env.VITE_WORKER_API_URL || 'https://shiksha-edge-api.workers.dev';
        
        const res = await fetch(`${API_URL}/api/reports/master-data`, {
          headers: { Authorization: `Bearer ${idToken}` }
        });
        
        if (!res.ok) throw new Error(`API error ${res.status}`);
        
        const data = await res.json();
        const apiStudents = data.students || [];
        const apiMarks = data.marks || [];
        
        // Map Google Sheets rows back to objects if necessary, assuming backend returns rows.
        // Wait, backend index.js returned `const students = await sheets.getRows(c.env.GOOGLE_SHEET_ID, 'Students!A:H');`
        // Those are raw arrays! I need to parse them.
        
        const parsedStudents = [];
        if (apiStudents.length > 1) {
          apiStudents.slice(1).forEach((row) => {
            const rollNo = parseInt(row[0]) || 0;
            const cls = row[2] || '';
            const section = row[3] || 'A';
            const stableId = row[8] || `legacy_${rollNo}_${cls}_${section}`.replace(/[^a-zA-Z0-9_]/g, '_');

            parsedStudents.push({
              id: stableId,
              rollNo,
              name: row[1] || '',
              class: cls,
              section,
              guardianName: row[4] || '',
              dob: row[5] || '',
              gender: row[6] || '',
              motherName: row[7] || '',
            });
          });
        }
        
        const parsedMarks = [];
        if (apiMarks.length > 1) {
          apiMarks.slice(1).forEach((row, i) => {
            parsedMarks.push({
              id: i + 1,
              studentId: row[0],
              subject: row[1] || '',
              term: row[2] || '',
              componentId: row[3] || '',
              score: Number(row[4]) || 0,
              maxScore: Number(row[5]) || 0,
            });
          });
        }

        setMasterStudents(parsedStudents);
        setMasterMarks(parsedMarks);
      } catch (err) {
        console.error("Failed to fetch master data:", err);
        setMasterDataError(err.message);
      } finally {
        setIsLoadingMasterData(false);
      }
    }
    
    fetchMasterData();
  }, []);

  const filteredStudents = masterStudents.filter(s => {
    const sClass = s.class?.toString() || '';
    if (targetClass === 'Class VI') return ['6','VI'].includes(sClass.toUpperCase());
    if (targetClass === 'Class VII') return ['7','VII'].includes(sClass.toUpperCase());
    if (targetClass === 'Class VIII') return ['8','VIII'].includes(sClass.toUpperCase());
    if (targetClass === 'Class IX') return ['9','IX'].includes(sClass.toUpperCase());
    if (targetClass === 'Class X') return ['10','X'].includes(sClass.toUpperCase());
    return true; // fallback
  }).filter(s => !s.section || s.section.toUpperCase() === section.toUpperCase());

  const filteredStudentIds = new Set(filteredStudents.map(s => s.id));
  const marks = masterMarks.filter(m => filteredStudentIds.has(m.studentId));
  
  const schemas = useLiveQuery(() => db.schemas.toArray()) || [];

  // Removed evaluateAST and getGrade since they are now in ClassReportTemplate

  const handleSelectAll = () => {
    if (selectedStudents.size === filteredStudents.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(filteredStudents.map(s => s.id)));
    }
  };

  const handleSelectOne = (id) => {
    const newSet = new Set(selectedStudents);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedStudents(newSet);
  };

  const [renderBatch, setRenderBatch] = useState([]);

  const handleGenerateSelected = async () => {
    if (selectedStudents.size === 0) return;
    setIsGenerating(true);
    
    const batchStudents = filteredStudents.filter(s => selectedStudents.has(s.id));
    setRenderBatch(batchStudents);
    
    // Wait for DOM to update and images to load
    await new Promise(resolve => setTimeout(resolve, 500)); 
    
    const element = reportTemplateRef.current;
    
    const opt = {
      margin:       10,
      filename:     `${targetClass}_${section}_Batch_Report_Cards.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true },
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    
    await html2pdf().from(element).set(opt).save();
    
    setIsGenerating(false);
    setRenderBatch([]);
  };

  return (
    <div className="min-h-screen bg-paper pb-8">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/admin/dashboard"
            className="p-2 rounded-lg bg-white border border-divider hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={20} className="text-ink" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-ink">Report Card Generator</h1>
            <p className="text-sm text-graphite font-secondary">Batch generate CBSE A4 report cards to PDF.</p>
          </div>
        </div>

        {/* ── Filters ── */}
        <div className="bg-white border border-divider rounded-xl p-5 mb-6 flex flex-wrap gap-4 items-end shadow-sm">
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Academic Year</label>
            <select
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none w-40"
            >
              <option value="2025-26">2025-26</option>
              <option value="2026-27">2026-27</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Class</label>
            <select
              value={targetClass}
              onChange={(e) => setTargetClass(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none w-40"
            >
              <option value="Class VI">Class VI</option>
              <option value="Class VII">Class VII</option>
              <option value="Class VIII">Class VIII</option>
              <option value="Class IX">Class IX</option>
              <option value="Class X">Class X</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Section</label>
            <select
              value={section}
              onChange={(e) => setSection(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none w-32"
            >
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
            </select>
          </div>
          <div className="flex-1"></div>
          <button
            onClick={handleGenerateSelected}
            disabled={selectedStudents.size === 0 || isGenerating}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
          >
            {isGenerating ? (
              <span className="animate-pulse">Generating...</span>
            ) : (
              <>
                <DownloadCloud size={16} />
                Generate Selected ({selectedStudents.size})
              </>
            )}
          </button>
        </div>

        {/* ── Student List ── */}
        {isLoadingMasterData ? (
          <div className="p-12 text-center text-graphite">Loading Master Sheet Data... (Requires Internet Connection)</div>
        ) : masterDataError ? (
          <div className="p-12 text-center text-red-600">Error: {masterDataError}. Please ensure you are online.</div>
        ) : (
        <div className="bg-white border border-divider rounded-xl shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-divider flex items-center gap-4">
            <button 
              onClick={handleSelectAll}
              className="text-indigo-600 hover:text-indigo-800"
            >
              {selectedStudents.size === filteredStudents.length && filteredStudents.length > 0 ? (
                <CheckSquare size={20} />
              ) : (
                <Square size={20} />
              )}
            </button>
            <span className="font-semibold text-sm text-ink">Select All Students</span>
          </div>
          
          <div className="divide-y divide-divider max-h-[500px] overflow-y-auto">
            {filteredStudents.map(student => (
              <div 
                key={student.id} 
                className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                onClick={() => handleSelectOne(student.id)}
              >
                <div className="text-indigo-600">
                  {selectedStudents.has(student.id) ? (
                    <CheckSquare size={20} />
                  ) : (
                    <Square size={20} />
                  )}
                </div>
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center font-bold text-indigo-700">
                  {student.rollNo}
                </div>
                <div>
                  <p className="font-bold text-ink">{student.name}</p>
                  <p className="text-xs text-graphite font-secondary">Guardian: {student.guardianName}</p>
                </div>
              </div>
            ))}
            
            {filteredStudents.length === 0 && (
              <div className="p-8 text-center text-graphite">
                No students found for this class and section.
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* ── Hidden DOM for PDF Rendering ── */}
      <div style={{ position: 'absolute', top: '-9999px', left: '-9999px' }}>
        <div ref={reportTemplateRef}>
          {renderBatch.map((student, index) => (
            <div key={student.id} style={{ pageBreakAfter: index < renderBatch.length - 1 ? 'always' : 'auto' }}>
              <ClassReportTemplate
                student={student}
                marks={marks}
                schemas={schemas}
                academicYear={academicYear}
                targetClass={targetClass}
                section={section}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
